import { Area, AreaChart as RechartsAreaChart, CartesianGrid, LabelList, Legend, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { ChartSeriesConfig, ReferenceLineConfig } from "../types";
import { useLegendToggle } from "./useLegendToggle";

// Gráfico de áreas preenchidas (réplica do "Treatment started vs finished" do Zoho): N séries desenhadas
// como áreas semi-transparentes sobrepostas. Mesma API do TimeSeriesChart (points + series por
// ChartSeriesConfig), com toggle de legenda click-to-hide e rótulos de valor por ponto opcionais.
type AreaChartProps = {
    points: Array<Record<string, string | number>>;
    series: Array<ChartSeriesConfig>;
    height?: number;
    showPointLabels?: boolean;
    referenceLine?: ReferenceLineConfig;
    yDomain?: [number | string, number | string];
    yTicks?: Array<number>;
    fillOpacity?: number;
};

export const AreaChart = ({ points, series, height = 380, showPointLabels = true, referenceLine, yDomain, yTicks, fillOpacity = 0.4 }: AreaChartProps) => {
    const { hiddenKeys, legendProps } = useLegendToggle();
    const hasRightAxis = series.some((serie) => serie.rightAxis);

    return (
        <ResponsiveContainer width="100%" height={height}>
            <RechartsAreaChart data={points} margin={{ top: 24, right: 24, bottom: 8, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                <XAxis dataKey="monthLabel" tick={{ fontSize: 12 }} />
                <YAxis yAxisId="left" domain={yDomain} ticks={yTicks} allowDataOverflow={Boolean(yDomain)} tick={{ fontSize: 12 }} />
                {hasRightAxis && <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 12 }} />}
                {referenceLine && (
                    <ReferenceLine
                        yAxisId="left"
                        y={referenceLine.value}
                        stroke={referenceLine.color || "#E25555"}
                        strokeDasharray="4 4"
                        label={referenceLine.label ? { value: referenceLine.label, position: "right", fontSize: 10, fill: referenceLine.color || "#E25555" } : undefined}
                    />
                )}
                <Tooltip />
                <Legend {...legendProps} />
                {series.map((serie) => (
                    <Area
                        key={serie.key}
                        yAxisId={serie.rightAxis ? "right" : "left"}
                        type="monotone"
                        dataKey={serie.key}
                        name={serie.label}
                        stroke={serie.color}
                        strokeWidth={serie.strokeWidth ?? 2}
                        fill={serie.color}
                        fillOpacity={fillOpacity}
                        dot={{ r: 3 }}
                        activeDot={{ r: 6 }}
                        hide={hiddenKeys.has(serie.key)}
                        connectNulls={false}
                        isAnimationActive={false}
                    >
                        {showPointLabels && <LabelList dataKey={serie.key} position="top" fontSize={10} fill={serie.color} />}
                    </Area>
                ))}
            </RechartsAreaChart>
        </ResponsiveContainer>
    );
};
